// Views will go here
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { IconNames } from '@dotcom/crown-ui';
import classNames from 'classnames';
import { AnimationPreviewDialog } from '../../components/animation-preview/animation-preview-dialog';
import { useAnimationPreview } from '../../components/animation-preview/hooks/use-animation-preview';
import { PlayAnimationButton } from '../../components/animation-preview/play-animation-button';
import { DrawerContainer } from '../../components/card-controls/drawer-container';
import {
  CardHistoryCoordinator,
  captureCanvasSnapshot,
  enqueueCanvasOperation,
  HistoryControlsAdapter,
  loadCanvasSnapshot,
  registerEditHistorySession,
} from '../../features/edit-history/edit-history';
import { isInsideImageEditToolbar, setImageEditToolbarHost } from '../../components/card-controls/image-edit-toolbar';
import { isInsideFontToolbar, setFontToolbarHost } from '../../components/card-controls/font-toolbar';
import {
  isInsidePhotoDrawer,
  requestPhotoDrawerClose,
  setPhotoDrawerHost,
} from '../../components/card-controls/photo-drawer/photo-drawer-host';
import { consumePendingOverlayReselect } from '../../features/card-editor/components/overlay-textbox/pending-overlay-reselect';
import {
  isInsideWamInstructionsMenu,
  setWamInstructionsMenuHost,
} from '../../components/card-controls/wam-instructions-menu/wam-instructions-menu-host';
import { CardEditor } from '../../components/card-editor';
import type { EditorHeaderActiveView } from '../../components/editor-header/editor-header.types';
import { CardFaceToggle } from '../../components/card-face-toggle';
import { CardSelectBar } from '../../components/card-select-bar';
import { ECardLimitModal } from '../../components/ecard-limit-modal/ecard-limit-modal';
import { FloatingToolMenu } from '../../components/floating-tool-menu/floating-tool-menu';
import { IconButton } from '../../components/icon-button/icon-button';
import { Layout } from '../../components/layout';
import { SEO } from '../../components/seo/seo';
import { useAnalyticsContext } from '../../context/analytics-context';
import { useAppContext } from '../../context/app-context';
import { CardPreview } from '../../features/card-preview/card-preview';
import type { CardPreviewState } from '../../features/card-preview/card-preview.types';
import { createCardPreviewState } from '../../features/card-preview/utils/create-card-preview-state';
import { useCardFaceReadinessStore } from '../../stores/card-face-readiness';
import { useUIStore } from '../../stores/ui';
import {
  hideLoadingScreen,
  setAllDrawersClosed,
  setCardFacesLoaded,
  setPhotoDrawerClosed,
} from '../../context/app-context/app-context-actions';
import { resetCardDataAction, useCardContext } from '../../context/card-context';
import { useCropContext } from '../../context/crop-context';
import { useInitializationDataContext } from '../../context/data-context';
import { RegionalCodesList } from '../../global-types';
import {
  useActiveCanvas,
  useExitUrl,
  useFeatureFlags,
  useIsDigitalGreeting,
  useIsSignAndSend,
  useLineItemUUID,
  useSanitizeSearchUrl,
} from '../../hooks';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { useIsRestoring } from '../../features/edit-history/edit-history-store';
import { useShowToolbar } from '../../components/toolbar/hooks/use-show-toolbar';
import { config } from '../../regional-config';
import { isToolbarVisible } from '../../utils';
import { logTemplateEvolvedFace, validateCardFacesAreNotEmpty } from '../../utils/canvas-utils';
import { isAnyOfRegions } from '../../utils/utility';
import styles from './editor.module.scss';
import { useEditorPreviewLayout, useHiddenEditorTransition, useLoadFontFaces, useStartProject } from './hooks';
import { useEditorNavigation } from './hooks/use-editor-navigation';
import { useAnimationPanels } from './hooks/use-animation-panels';
import { useUnsavedChanges } from './hooks/use-unsaved-changes';
import { getClassEditorOverlapStyle } from './utils/get-class-editor-overlap-style';
import { getFloatingMenuFaceType, getShouldShowFloatingMenu } from './utils/get-should-show-floating-menu';
const createEditorSessionKey = (): string => `edit-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const EditorView = (): ReactElement => {
  const { pathname } = useLocation();
  const {
    cardState,
    cardState: { slideCardIndex, activeCardIndex, cardFacesList },
    cardDispatch,
  } = useCardContext();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const isOneToMany = useIsOneToMany();

  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);
  const [triggerPageLoadTracking, setTriggerPageLoadTracking] = useState(false);
  const [triggerPageRefreshTracking, setTriggerPageRefreshTracking] = useState(false);
  const [eCardLimitModal, setECardLimitModal] = useState(false);
  const [editorActiveView, setEditorActiveView] = useState<EditorHeaderActiveView>('edit');
  const [editorSessionKey, setEditorSessionKey] = useState(createEditorSessionKey);
  const [pendingHistoryRestoreSessionKey, setPendingHistoryRestoreSessionKey] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<CardPreviewState>();
  const isDataLoaded = useRef<boolean>(false);
  const faceNavigationRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const activeCanvas = useActiveCanvas();
  const isSignAndSend = useIsSignAndSend();
  const [isEditorToolbarVisible, setIsEditorToolbarVisible] = useState(false);
  const textEditorDrawerType = config?.cardEditor?.textEditorDrawerType;
  const footerContainerClass = config?.cardSelectBar?.containerClass;

  const isTextDrawerOpen = useUIStore((state) => state.isTextDrawerOpen);
  const closeImageEditToolbar = useUIStore((state) => state.closeImageEditToolbar);
  const isImageEditToolbarOpen = useUIStore((state) => state.isImageEditToolbarOpen);
  const closeFontToolbar = useUIStore((state) => state.closeFontToolbar);
  const closeAllToolbars = useUIStore((state) => state.closeAllToolbars);
  const faceReadiness = useCardFaceReadinessStore((state) => state.faceReadiness);
  const setFaceReadiness = useCardFaceReadinessStore((state) => state.setFaceReadiness);
  const resetFaceReadiness = useCardFaceReadinessStore((state) => state.resetFaceReadiness);

  const {
    appState: {
      isSystemErrorOpen,
      cardFacesLoaded,
      isImageUploadDrawerOpen,
      isImageUploadToPhotoZoneOpen,
      isImageEditDrawerOpen,
      personalizationData,
    },
    appDispatch,
  } = useAppContext();
  const {
    trackClickPreviewMode,
    trackStartFlow,
    firstElementSelected,
    trackPageNavigation,
    trackRefresh,
    trackDGPlayAnimation,
  } = useAnalyticsContext();

  const { isCropping } = useCropContext();
  const isHistoryRestoring = useIsRestoring();
  const isAddressRestorePending = pendingHistoryRestoreSessionKey === editorSessionKey;

  useEffect(() => {
    if (isHistoryRestoring && !isAddressRestorePending) {
      setPendingHistoryRestoreSessionKey(editorSessionKey);
      return;
    }
    if (!isHistoryRestoring && cardFacesLoaded && isAddressRestorePending) {
      setPendingHistoryRestoreSessionKey(null);
    }
  }, [cardFacesLoaded, editorSessionKey, isAddressRestorePending, isHistoryRestoring]);

  const isAddressRestoring = isHistoryRestoring || isAddressRestorePending;

  const isCreateView = useMemo(() => pathname.indexOf('create') >= 0, [pathname]);
  // PRCM-7675: capture whether the user landed in the editor via /create/ on the very
  // first render. The router flips /create/{productId}/ -> /edit/{projectId}/ mid-mount
  // (once initializedData?.product_id resolves), which used to race against isCreateView
  // in the trackStartFlow effect and prevent the Flow Start tag from firing.
  const startedFromCreateViewRef = useRef<boolean | null>(null);
  if (startedFromCreateViewRef.current === null) {
    startedFromCreateViewRef.current = pathname.indexOf('create') >= 0;
  }
  const pageTitle = useMemo(
    () => (isCreateView ? t('editorView.pageTitle') : t('editorView.editPageTitle')),
    [isCreateView],
  );
  const isDigitalGreeting = useIsDigitalGreeting();
  const {
    IS_EDITOR_HEADER_ENABLED,
    IS_LOYALTY_ENABLED,
    IS_EDITOR_NAVIGATION_ENABLED,
    IS_EDITOR_UNDO_REDO_ENABLED,
    IS_FLOATING_MENU_ENABLED,
    IS_PHOTO_DRAWER_REDESIGN_ENABLED,
    IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
    IS_ECARD_SEND_ENABLED,
  } = useFeatureFlags();
  const [editorClass, setEditorClass] = useState(styles['editor']);
  useLineItemUUID();
  useExitUrl();
  const { isSearchUrlSanitized } = useSanitizeSearchUrl();

  const currentCardFace = cardFacesList[`${activeCardIndex}`];
  const isFrontFace = currentCardFace?.type === 'front';
  const { showToolbar, selectedZoneHasContent } = useShowToolbar();

  const shouldShowFloatingMenu = getShouldShowFloatingMenu({
    isFloatingMenuEnabled: IS_FLOATING_MENU_ENABLED,
    isCropping,
    isSignAndSend,
    isOneToMany,
    currentFaceType: getFloatingMenuFaceType(currentCardFace?.type),
  });

  const editableFaces = useMemo(() => cardFacesList.filter((face) => face.editorDisplayIndicator), [cardFacesList]);
  const isPreviewMode = editorActiveView === 'preview';

  // eCards can play their animation straight from the editor quick preview,
  // reusing the shared dialog/hook that also drives the preview view.
  const projectId = initializedData?.project_id ?? sessionStorage.getItem('lastProjectLoaded') ?? '';
  const previousProjectIdRef = useRef(projectId);

  const { animationPreviewImages, closeAnimation, isAnimationDialogOpen, playAnimation } = useAnimationPreview({
    projectId,
    onPlay: trackDGPlayAnimation,
  });
  const isAnimationPreviewVisible = Boolean(IS_ECARD_SEND_ENABLED && isDigitalGreeting && isPreviewMode);
  const { buttonProps, handleIndexChange, handleProjectDone, handleAfterSlide } = useEditorNavigation();
  const pendingHistoryNavigationRef = useRef<{ faceIndex: number; resolve: () => void } | null>(null);
  const historyReadinessGenerationsRef = useRef(new Map<string, number>());
  const revealEditor = useCallback(() => {
    setEditorActiveView('edit');
  }, []);
  const { handleTransitionSettled, startTransition, waitForTransition } = useHiddenEditorTransition(revealEditor);
  const { captureEditorPreviewLayout, editorWorkspaceRef, previewLayout, workspacePanelRef } = useEditorPreviewLayout();
  const { hasUnsavedChanges, resetBaselineAfterSave, setInitialBaseline, baselinePersonalizationRef } =
    useUnsavedChanges({
      initializedData,
      cardState,
      cardFacesList,
      isOneToMany,
      persistedPersonalizationData: personalizationData,
    });
  const { isPreparingPanels } = useAnimationPanels({
    isEnabled: isAnimationPreviewVisible,
    projectId,
    hasUnsavedChanges,
    onPanelsPrepared: resetBaselineAfterSave,
  });

  useEffect(() => {
    const previousProjectId = previousProjectIdRef.current;
    if (previousProjectId && projectId && previousProjectId !== projectId) {
      resetFaceReadiness();
      historyReadinessGenerationsRef.current.clear();
      setCardFacesLoaded(appDispatch, false);
      setEditorSessionKey(createEditorSessionKey());
    }
    previousProjectIdRef.current = projectId;
  }, [projectId, cardDispatch, appDispatch, resetFaceReadiness]);

  // TODO: Move this to a custom hook to clean the component
  /**
   * First function to be called when user open the page, which
   * kicks off loading of project assets
   */
  const startProject = useStartProject(isDataLoaded, [pathname, isSearchUrlSanitized], isOneToMany);
  const { loadDeferredFonts } = useLoadFontFaces();

  const getCurrentCardPreviewState = useCallback(
    () =>
      createCardPreviewState({
        cardFacesList,
        faceVisibility: 'editor',
        project: initializedData ?? undefined,
      }),
    [cardFacesList, initializedData],
  );

  const onChangeIndex = (newIndex: number) => {
    const selectedFace = editableFaces[`${newIndex}`];
    if (!selectedFace) {
      return;
    }

    const selectedActiveCardIndex = selectedFace.faceNumber - 1;
    // Close all toolbars FIRST so that the subsequent `selection:cleared`
    // event (from discardActiveObject) doesn't trigger toolbar re-renders.
    closeAllToolbars();
    activeCanvas?.current?.discardActiveObject();
    activeCanvas?.current?.requestRenderAll();
    handleIndexChange(newIndex, selectedActiveCardIndex);
  };

  const handlePreviewNavigationChange = (index: number) => {
    const selectedFace = editableFaces[`${index}`];
    if (!selectedFace || index === slideCardIndex) {
      return;
    }

    handleIndexChange(index, selectedFace.faceNumber - 1);
    startTransition(index);
  };

  const handleEditorAfterSlide = (newIndex: number) => {
    handleAfterSlide();
    if (pendingHistoryNavigationRef.current?.faceIndex === newIndex) {
      pendingHistoryNavigationRef.current.resolve();
      pendingHistoryNavigationRef.current = null;
    }
    handleTransitionSettled(newIndex);
  };

  const historyNavigationStateRef = useRef({
    cardFacesList,
    cardState,
    editableFaces,
    slideCardIndex,
    handleIndexChange,
  });
  historyNavigationStateRef.current = {
    cardFacesList,
    cardState,
    editableFaces,
    slideCardIndex,
    handleIndexChange,
  };

  const historyCoordinator = useMemo(() => {
    const coordinator = new CardHistoryCoordinator({
      captureSnapshot: (canvas) => captureCanvasSnapshot(canvas as Parameters<typeof captureCanvasSnapshot>[0]),
      getCanvas: (identity) =>
        historyNavigationStateRef.current.cardFacesList.find(
          (face) =>
            face.faceId === identity.faceId && face.faceNumber === identity.faceNumber && face.type === identity.type,
        )?.canvas.current ?? undefined,
      restoreSnapshot: async (identity, snapshot) => {
        const targetFace = historyNavigationStateRef.current.cardFacesList.find(
          (face) =>
            face.faceId === identity.faceId && face.faceNumber === identity.faceNumber && face.type === identity.type,
        );
        const canvas = targetFace?.canvas.current;
        if (!canvas) {
          throw new Error('History target canvas is unavailable');
        }
        await enqueueCanvasOperation(canvas, async () => {
          await loadCanvasSnapshot(canvas, snapshot);
          canvas.requestRenderAll();
        });
      },
      navigateToFace: async (identity) => {
        const current = historyNavigationStateRef.current;
        const targetIndex = current.editableFaces.findIndex(
          (face) =>
            face.faceId === identity.faceId && face.faceNumber === identity.faceNumber && face.type === identity.type,
        );
        if (targetIndex < 0) {
          throw new Error('History target face is not editable');
        }
        if (targetIndex === current.slideCardIndex) {
          return;
        }

        await new Promise<void>((resolve) => {
          pendingHistoryNavigationRef.current = { faceIndex: targetIndex, resolve };
          current.handleIndexChange(targetIndex, identity.faceNumber - 1, { suppressAutosave: true });
        });
      },
      isFaceReady: (identity) =>
        useCardFaceReadinessStore
          .getState()
          .faceReadiness.some(
            (face) =>
              face.editorSessionKey === identity.editorSessionKey &&
              face.faceId === identity.faceId &&
              face.faceNumber === identity.faceNumber &&
              face.type === identity.type &&
              face.status === 'ready',
          ),
      setFaceReadiness: (identity, status) => {
        const identityKey = JSON.stringify([
          identity.editorSessionKey,
          identity.faceId,
          identity.faceNumber,
          identity.type,
        ]);
        const currentFace = useCardFaceReadinessStore
          .getState()
          .faceReadiness.find(
            (face) =>
              face.editorSessionKey === identity.editorSessionKey &&
              face.faceId === identity.faceId &&
              face.faceNumber === identity.faceNumber &&
              face.type === identity.type,
          );
        const currentGeneration =
          historyReadinessGenerationsRef.current.get(identityKey) ?? currentFace?.generation ?? 0;
        const generation = status === 'loading' ? currentGeneration + 1 : currentGeneration || 1;
        historyReadinessGenerationsRef.current.set(identityKey, generation);
        setFaceReadiness({ ...identity, status, generation });
      },
      markFaceFatal: (identity, error) => {
        const currentFace = useCardFaceReadinessStore
          .getState()
          .faceReadiness.find(
            (face) =>
              face.editorSessionKey === identity.editorSessionKey &&
              face.faceId === identity.faceId &&
              face.faceNumber === identity.faceNumber &&
              face.type === identity.type,
          );
        setFaceReadiness({ ...identity, status: 'fatal', generation: currentFace?.generation ?? 1 });
        pendingHistoryNavigationRef.current?.resolve();
        pendingHistoryNavigationRef.current = null;
        void error;
      },
    });

    return coordinator;
  }, [editorSessionKey, setFaceReadiness]);

  useEffect(() => {
    if (!historyCoordinator) return undefined;
    return registerEditHistorySession(historyCoordinator, editorSessionKey, (canvas) => {
      const cardFace = historyNavigationStateRef.current.cardFacesList.find((face) => face.canvas?.current === canvas);
      if (!cardFace) return null;
      return {
        editorSessionKey,
        faceId: cardFace.faceId,
        faceNumber: cardFace.faceNumber,
        type: cardFace.type,
      };
    });
  }, [editorSessionKey, historyCoordinator]);

  const historyLifetimeRef = useRef(new WeakMap<CardHistoryCoordinator, object>());

  useEffect(() => {
    if (!historyCoordinator) {
      return;
    }
    const lifetime = {};
    historyLifetimeRef.current.set(historyCoordinator, lifetime);
    return () => {
      queueMicrotask(() => {
        if (historyLifetimeRef.current.get(historyCoordinator) === lifetime) {
          historyCoordinator.dispose();
        }
      });
    };
  }, [historyCoordinator]);

  useEffect(() => {
    historyCoordinator?.refresh();
  }, [historyCoordinator, faceReadiness]);

  const handleEditorViewChange = (view: EditorHeaderActiveView) => {
    if (view === 'preview') {
      closeAllToolbars();
      activeCanvas?.current?.discardActiveObject();
      activeCanvas?.current?.requestRenderAll();

      const selectedEditorFace = editableFaces[`${slideCardIndex}`];
      if (selectedEditorFace) {
        captureEditorPreviewLayout({
          faceNumber: selectedEditorFace.faceNumber,
          isSignAndSend,
        });
      }
      const nextPreviewState = getCurrentCardPreviewState();
      setPreviewState(nextPreviewState);
      setEditorActiveView('preview');
      return;
    }

    if (waitForTransition()) {
      return;
    }
    revealEditor();
  };

  const handleClickOutsideCanvas = (ev: MouseEvent | TouchEvent) => {
    const target = ev.target as Element | null;

    // PRCM-10837: skip once after overlay blur schedules a rAF reselect.
    if (consumePendingOverlayReselect()) {
      return;
    }

    if (isInsideImageEditToolbar(target)) {
      return;
    }

    if (isInsideFontToolbar(target)) {
      return;
    }

    if (isInsidePhotoDrawer(target)) {
      return;
    }

    if (isInsideWamInstructionsMenu(target)) {
      return;
    }

    // Clicks inside the legacy ImageEditDrawer are interactions with the drawer
    // itself (its buttons dispatch their own actions), not outside dismissals.
    if (target?.closest('[data-testid="image-edit-drawer"]')) {
      return;
    }

    // PRCM-10916: crown-ui dialogs (e.g. the replace-photo confirmation) portal
    // to document.body, outside the workspace and toolbar hosts. A click on any
    // open modal (its content, buttons, or overlay) is a modal interaction, not
    // an outside-canvas dismissal — so it must not close the drawers underneath.
    if (target?.closest('[role="dialog"], [data-testid="dialog-overlay"]')) {
      return;
    }

    // PRCM-10837: clicks inside fabric's `.canvas-container` wrapper (or on a
    // textarea) are canvas interactions, not outside-canvas dismissals.
    if (target?.closest('canvas, textarea, .canvas-container')) {
      return;
    }

    // Mobile Safari doesn't blur focused <textarea> on outside taps like
    // desktop does. Blur explicitly so `onTextareaBlur` hides the overlay.
    // Only runs for genuine outside-canvas taps (inside-canvas taps are
    // handled by fabric's own mouse:up path via use-canvas-mouse-up).
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLTextAreaElement && activeElement.id.startsWith('overlay-textarea')) {
      activeElement.blur();
      // POD's rAF reselect handles the transition to select mode; nothing
      // more to do here.
      return;
    }

    // Close toolbars before discardActiveObject so that the synchronous
    // Zustand store update is visible to ImageEditToolbar's selection:cleared
    // listener before it fires. This prevents restoreCachedSelection from
    // reselecting the image while the toolbar is already being dismissed.

    // PRCM-10916: single click-outside dispatcher. Dismiss only the top-most
    // open drawer so repeated outside clicks peel them off one at a time.
    // 1) The photo ("Your photos") drawer always sits on top when open. Close
    //    it via its animated handler and leave any selection / edit surface
    //    beneath it untouched (fall back to a plain dispatch if the drawer is
    //    not currently mounted to register its handler).
    if (isImageUploadDrawerOpen || isImageUploadToPhotoZoneOpen) {
      if (!requestPhotoDrawerClose()) {
        setPhotoDrawerClosed(appDispatch);
      }
      return;
    }

    // 2) Edit surface — close it top-most so it is dismissed by clicks anywhere
    //    outside the canvas (header / footer / page chrome included). Skip while
    //    cropping (crop has its own apply / cancel controls).
    //    - New ImageEditToolbar (flag on): close via its store action and
    //      deselect so its own selection:cleared listener settles.
    //    - Legacy ImageEditDrawer (flag off): not wired to the toolbar store,
    //      so close it explicitly.
    if (IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED) {
      if (isImageEditToolbarOpen && !isCropping) {
        closeImageEditToolbar();
        closeFontToolbar();
        activeCanvas?.current?.discardActiveObject();
        activeCanvas?.current?.requestRenderAll();
        return;
      }
    } else if (isImageEditDrawerOpen && !isCropping) {
      setAllDrawersClosed(appDispatch);
      return;
    }

    // 3) Generic selection / font-toolbar dismissal (existing behaviour).
    //    Restricted to clicks inside the workspace panel to match the
    //    pre-existing scope — header / page-chrome clicks never deselect the
    //    active object on their own; they only close the drawers handled above.
    if (!workspacePanelRef.current?.contains(target)) {
      return;
    }
    closeImageEditToolbar();
    closeFontToolbar();
    activeCanvas?.current?.discardActiveObject();
    activeCanvas?.current?.requestRenderAll();
  };

  useEffect(() => {
    if (isDataLoaded.current) {
      return;
    }
    // reset card state re-load canvas if moving back to edit
    resetCardDataAction(cardDispatch);
    resetFaceReadiness();
    // canvases are torn down on re-entry, so mark faces as not-yet-loaded to re-arm the save guard
    setCardFacesLoaded(appDispatch, false);
    // initialize or load project and save the response in corresponding context
    startProject();
  }, [startProject, isDataLoaded.current]);

  useEffect(() => {
    // We save this so we can load the project from the address view if the page is refreshed
    sessionStorage.setItem('lastProjectLoaded', initializedData?.project_id || '');
  }, [initializedData?.project_id]);

  useEffect(() => {
    // Additional saved info in case of address view refresh
    sessionStorage.setItem('lastProjectTypeCode', initializedData?.project_type_code || '');
  }, [initializedData?.project_type_code]);

  useEffect(() => {
    if (cardFacesLoaded) {
      hideLoadingScreen(appDispatch);
      loadDeferredFonts();
    }
  }, [cardFacesLoaded]);

  useEffect(() => {
    if (!cardFacesLoaded || baselinePersonalizationRef.current) {
      return;
    }

    void setInitialBaseline(personalizationData);
  }, [cardFacesLoaded, slideCardIndex, activeCardIndex, initializedData?.project_id, personalizationData]);

  useEffect(() => {
    if (personalizationData.length > 0) {
      resetBaselineAfterSave(personalizationData);
    }
  }, [personalizationData, resetBaselineAfterSave]);

  useEffect(() => {
    setTriggerPageLoadTracking(true);
  }, []);

  useEffect(() => {
    // PRCM-7675: use startedFromCreateViewRef instead of the live isCreateView.
    // The router flips /create/ -> /edit/ before initializedData?.product_id resolves,
    // which used to make (hasProductId && isCreateView) mutually exclusive and prevent
    // the Flow Start tag from ever firing on first load.
    const startedFromCreateView = startedFromCreateViewRef.current === true;

    if (initializedData?.product_id && isInitialPageLoad && triggerPageLoadTracking) {
      if (startedFromCreateView) {
        trackStartFlow(isDigitalGreeting ? 'Digital Greetings' : 'Print on Demand');
      }
      setIsInitialPageLoad(false);
    }
    if (!initializedData) {
      setTriggerPageRefreshTracking(true);
    }
  }, [initializedData, isInitialPageLoad, triggerPageLoadTracking, isCreateView]);

  useEffect(() => {
    // this is set to track the slide changes before any elements are edited or selected
    if (!firstElementSelected.current) {
      trackClickPreviewMode();
    } else {
      trackPageNavigation();
    }
  }, [activeCardIndex]);

  useEffect(() => {
    const clickOrTouch = 'ontouchstart' in window ? 'touchstart' : 'click';
    // Attach on `document` (capture phase) so clicks anywhere outside the
    // canvas — including the editor header and page chrome, which live outside
    // the workspace panel — are caught. Capture runs before React's synthetic
    // handlers (which can re-render and detach `ev.target`, breaking
    // `host.contains(target)` in a bubble-phase listener).
    document.addEventListener(clickOrTouch, handleClickOutsideCanvas, true);
    if (activeCanvas) {
      setIsEditorToolbarVisible(isToolbarVisible(activeCanvas, isSignAndSend));
      if (triggerPageRefreshTracking && isDataLoaded.current) {
        (window.performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]).forEach((entry) => {
          if (entry.type === 'reload') {
            trackRefresh();
          }
        });
        setTriggerPageRefreshTracking(false);
      }
    }
    return () => document.removeEventListener(clickOrTouch, handleClickOutsideCanvas, true);
  }, [
    activeCanvas,
    isDataLoaded.current,
    cardFacesLoaded,
    isImageUploadDrawerOpen,
    isImageUploadToPhotoZoneOpen,
    isImageEditDrawerOpen,
    isImageEditToolbarOpen,
    isCropping,
  ]);

  const seoMetaProperties = [
    {
      property: 'og:title',
      content: pageTitle,
    },
    {
      property: 'og:site_name',
      content: pageTitle,
    },
  ];
  const shouldShowFooterAddressButton =
    !IS_EDITOR_HEADER_ENABLED &&
    !isSystemErrorOpen &&
    !isCropping &&
    isAnyOfRegions([RegionalCodesList.uk, RegionalCodesList.us]);
  const FaceNavigation = IS_EDITOR_NAVIGATION_ENABLED ? CardFaceToggle : CardSelectBar;
  const handleFaceNavigationChange = isPreviewMode ? handlePreviewNavigationChange : onChangeIndex;

  useEffect(() => {
    const updateEditorClass = () => {
      const newEditorClass = getClassEditorOverlapStyle(
        styles,
        isTextDrawerOpen,
        activeCanvas,
        isMobile,
        isFrontFace,
        IS_PHOTO_DRAWER_REDESIGN_ENABLED ? false : isImageUploadDrawerOpen,
        IS_PHOTO_DRAWER_REDESIGN_ENABLED ? false : isImageUploadToPhotoZoneOpen,
        textEditorDrawerType,
        isSignAndSend,
      );
      setEditorClass(newEditorClass);
    };

    const timeout = setTimeout(updateEditorClass, 50);

    return () => clearTimeout(timeout);
  }, [isTextDrawerOpen, isImageUploadDrawerOpen, isImageUploadToPhotoZoneOpen]);

  useEffect(() => {
    if (
      cardFacesLoaded &&
      isDataLoaded.current &&
      initializedData?.variables.template_data &&
      cardFacesList.length > 0
    ) {
      const results = validateCardFacesAreNotEmpty(cardFacesList, initializedData?.variables.template_data);
      results.forEach((result) => {
        // Recoverable: canvas has fewer permanent objects than the current
        // template requires (typically because the template evolved after the
        // project was saved). Intentionally log the divergence to Datadog RUM
        // for observability and let the user continue editing.
        logTemplateEvolvedFace('editor', result);
      });
    }
  }, [cardFacesLoaded, isDataLoaded.current]);

  return (
    <Layout
      hideToolbar={isPreviewMode || !isEditorToolbarVisible || IS_FLOATING_MENU_ENABLED}
      editorHeaderProps={{
        activeView: editorActiveView,
        onViewChange: handleEditorViewChange,
        onAddressClick: () => handleProjectDone(),
        isAddressDisabled: isCropping || !cardFacesLoaded,
        isAddressRestoring,
        isSaveDisabled: !cardFacesLoaded,
        hasUnsavedChanges,
        onSaveSuccess: resetBaselineAfterSave,
      }}
    >
      <SEO title={pageTitle} description={`${t('editorView.seo')}`} metaProperties={seoMetaProperties} />
      <DrawerContainer />
      <div
        className={classNames(
          editorClass,
          IS_FLOATING_MENU_ENABLED && styles['editor--with-floating-menu'],
          isPreviewMode && styles['editor--preview'],
          isSignAndSend && cardState.cardFormat === 'landscape' && styles['editor--landscape-sas'],
        )}
      >
        <div
          className={
            !isOneToMany ? styles['workspace-panel'] : `${styles['workspace-panel']} ${styles['is-one-to-many']}`
          }
          data-testid="Workspace"
          id="Workspace"
          ref={workspacePanelRef}
        >
          <div
            className={classNames(styles['editor-workspace'], isPreviewMode && styles['editor-workspace--hidden'])}
            aria-hidden={isPreviewMode}
            ref={editorWorkspaceRef}
          >
            {!isPreviewMode && historyCoordinator && IS_EDITOR_UNDO_REDO_ENABLED && (
              <div className={styles['history-controls-slot']}>
                <HistoryControlsAdapter />
              </div>
            )}
            <div className={styles['editor-card-editor']}>
              <CardEditor
                cardFaceIndex={slideCardIndex}
                editorSessionKey={editorSessionKey}
                navigationRef={faceNavigationRef}
                onAfterSlide={handleEditorAfterSlide}
              />
            </div>
            <div
              ref={setImageEditToolbarHost}
              className={styles['image-edit-drawer-host']}
              data-testid="image-edit-drawer-host"
            />
            <div ref={setFontToolbarHost} className={styles['font-toolbar-host']} data-testid="font-toolbar-host" />
            <div
              ref={setWamInstructionsMenuHost}
              className={styles['wam-instructions-menu-host']}
              data-testid="wam-instructions-menu-host"
            />
            <div ref={setPhotoDrawerHost} className={styles['photo-drawer-host']} data-testid="photo-drawer-host" />
          </div>
          {isPreviewMode && previewState && (
            <CardPreview
              className={styles['preview-mode']}
              previewState={previewState}
              showNavigation={false}
              showSurfaceChrome={false}
              variant="embedded"
              activeSurfaceIndex={slideCardIndex}
              targetSurfaceWidth={previewLayout.targetSurfaceWidth}
              targetSurfaceHeight={previewLayout.targetSurfaceHeight}
              targetSurfaceTop={previewLayout.targetSurfaceTop}
              targetSurfaceBottom={previewLayout.targetSurfaceBottom}
            />
          )}
        </div>
        <div
          className={classNames(
            styles[footerContainerClass ?? 'footer'],
            IS_FLOATING_MENU_ENABLED && styles['footer-with-floating-menu'],
          )}
        >
          {cardFacesList && !isCropping && (
            <div
              className={styles['card-select-bar']}
              data-testid="editor-card-face-navigation"
              ref={faceNavigationRef}
            >
              <FaceNavigation
                faces={editableFaces}
                slideIndex={slideCardIndex}
                handleIndexChange={handleFaceNavigationChange}
                mode="edit"
              />
            </div>
          )}
          {!isPreviewMode && shouldShowFooterAddressButton && (
            <IconButton
              {...buttonProps}
              variant="primary"
              testId="address-button"
              className={styles['address-button']}
              labelClassName={styles['address-button-label']}
              iconClassName={styles['address-button-icon']}
              iconPosition="right"
              label={buttonProps.text}
              iconName={IconNames.ArrowsTailRightBold}
              iconColor="white"
              iconSize="14"
            />
          )}
          {(IS_FLOATING_MENU_ENABLED || isAnimationPreviewVisible) && (
            <div
              className={classNames(
                styles['floating-menu-slot'],
                isAnimationPreviewVisible && styles['floating-menu-slot-centered'],
                (isPreviewMode || !shouldShowFloatingMenu) &&
                  !isAnimationPreviewVisible &&
                  styles['floating-menu-slot-hidden'],
              )}
            >
              {isAnimationPreviewVisible ? (
                <PlayAnimationButton
                  isPreparing={isPreparingPanels}
                  label={t('previewView.playAnimation')}
                  onClick={() => void playAnimation()}
                />
              ) : (
                IS_FLOATING_MENU_ENABLED && (
                  <FloatingToolMenu
                    isSignAndSend={isSignAndSend}
                    isOpen={isSignAndSend ? showToolbar : undefined}
                    selectedZoneHasContent={selectedZoneHasContent}
                  />
                )
              )}
            </div>
          )}
        </div>
        {IS_LOYALTY_ENABLED && <ECardLimitModal onClose={() => setECardLimitModal(false)} isOpen={eCardLimitModal} />}
        {isAnimationDialogOpen && (
          <AnimationPreviewDialog
            isOpen={isAnimationDialogOpen}
            panelImages={animationPreviewImages}
            onClose={closeAnimation}
          />
        )}
      </div>
    </Layout>
  );
};
